import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from './cn';

const SIZES = {
  sm: 'max-w-md',
  base: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  full: 'max-w-[95vw]',
};

/**
 * Modal — hộp thoại dùng chung (Headless UI Dialog).
 * Tự có: focus-trap, đóng bằng ESC, khoá scroll nền, role="dialog"/aria-modal, blur backdrop.
 *
 * Props: open, onClose, title, children, footer, size, closeOnOverlay, hideClose, className, bodyClassName.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'base',
  closeOnOverlay = true,
  hideClose = false,
  className,
  bodyClassName,
}) {
  return (
    <Transition show={!!open} as={Fragment}>
      <Dialog onClose={closeOnOverlay ? onClose : () => {}} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel
                className={cn(
                  'w-full flex flex-col max-h-[90vh] rounded-xl border border-line bg-surface-raised shadow-xl',
                  SIZES[size] || SIZES.base,
                  className
                )}
              >
                {title !== undefined && title !== null ? (
                  <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-line flex-shrink-0">
                    <Dialog.Title className="text-lg font-semibold text-heading">{title}</Dialog.Title>
                    {!hideClose ? (
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label="Đóng"
                        className="-mr-1.5 rounded-lg p-1.5 text-muted hover:bg-surface-sunken hover:text-heading transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className={cn('px-6 py-5 overflow-y-auto', bodyClassName)}>{children}</div>

                {footer ? (
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-line flex-shrink-0">
                    {footer}
                  </div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default Modal;
